import { SaveWaitlistDto, WaitlistDto } from 'actions/waitlists/WaitlistsDtos';
import moment, { Moment } from 'moment-timezone';
import { SaveWaitlist, Waitlist, WaitlistTime } from 'types/Waitlist';
import { formatWaitlistTimeId, parseWaitlistTimeId } from 'utils/waitlistUtils';

const DATE_FORMAT = 'YYYY-MM-DD';

const toNumber = (value?: number | string | null): number | undefined => {
    if (value === null || value === undefined || value === '') {
        return undefined;
    }

    const parsed = Number(value);

    return Number.isFinite(parsed) ? parsed : undefined;
};

const convertToDate = (date: string | null): Moment | null => {
    if (!date) {
        return null;
    }

    const parsed = moment(date, DATE_FORMAT);

    return parsed.isValid() ? parsed : null;
};

// Dates go as `YYYY-MM-DD`, times as `HH:MM` (24h). `null` is the "any" value, never "".
const formatDate = (date: Moment | null): string | null => (date ? date.format(DATE_FORMAT) : null);

const formatTime = (time: WaitlistTime | null): string | null =>
    time ? formatWaitlistTimeId(time) : null;

export const convertToWaitlist = (dto: WaitlistDto): Waitlist => {
    return {
        id: dto.id,
        uuid: dto.uuid,
        marketplaceId: dto.marketplace_id,
        customerId: dto.customer_id,
        status: dto.status,

        preferredTime: dto.preferred_time ? parseWaitlistTimeId(dto.preferred_time) : null,
        validUntil: convertToDate(dto.valid_until),

        notes: dto.notes ?? '',
        price: toNumber(dto.price),

        services: (dto.waitlist_services ?? []).map(serviceDto => ({
            id: serviceDto.id,
            petId: serviceDto.pet_id,
            serviceId: serviceDto.service_id,
            price: toNumber(serviceDto.price) ?? null,
            preferredStaffId: serviceDto.preferred_staff_id ?? null,
            dates: serviceDto.dates ?? [],
            addons: (serviceDto.waitlist_addons ?? []).map(addonDto => ({
                id: addonDto.id,
                addonServiceId: addonDto.addon_service_id,
                price: toNumber(addonDto.price) ?? null
            }))
        }))
    };
};

export const convertToSaveWaitlistDto = (waitlist: SaveWaitlist): SaveWaitlistDto => {
    return {
        marketplace_id: waitlist.marketplaceId,
        waitlist: {
            preferred_time: formatTime(waitlist.preferredTime),
            valid_until: formatDate(waitlist.validUntil),
            notes: waitlist.notes,
            waitlist_services_attributes: waitlist.services.map(service => ({
                pet_id: service.petId,
                service_id: service.serviceId,
                price: service.price,
                preferred_staff_id: service.preferredStaffId ?? null,
                dates: service.dates,
                waitlist_addons_attributes: service.addons.map(addon => ({
                    addon_service_id: addon.addonServiceId,
                    price: addon.price
                }))
            }))
        }
    };
};
